import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove, serverTimestamp, deleteField, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FeedPost, FeedComment, ClassSchedule } from '../types';
import { useAuth } from '../contexts/AuthContext';

export const useFeed = () => {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const initialized = useRef(false);

  // One-time fetch for initial data (no real-time updates)
  const fetchInitialData = useCallback(async () => {
    if (loading || initialized.current) {
      console.log('[useFeed] Skipping fetch - loading:', loading, 'initialized:', initialized.current);
      return;
    }
    
    console.log('[useFeed] Starting to fetch initial data...');
    setLoading(true);
    try {
      // Fetch posts
      const postsQuery = query(
        collection(db, 'feed_posts'),
        orderBy('createdAt', 'desc'),
        limit(20) // Reduced from 50 for initial load
      );
      const postsSnapshot = await getDocs(postsQuery);
      const postsData: FeedPost[] = [];
      postsSnapshot.forEach((doc) => {
        postsData.push({ id: doc.id, ...doc.data() } as FeedPost);
      });
      console.log('[useFeed] Fetched posts:', postsData.length);
      setPosts(postsData);

      // Fetch schedules
      const schedulesQuery = query(
        collection(db, 'class_schedules'),
        where('status', 'in', ['scheduled', 'ongoing']),
        orderBy('scheduledTime', 'asc'),
        limit(20) // Reduced from 50 for initial load
      );
      const schedulesSnapshot = await getDocs(schedulesQuery);
      const schedulesData: ClassSchedule[] = [];
      schedulesSnapshot.forEach((doc) => {
        schedulesData.push({ id: doc.id, ...doc.data() } as ClassSchedule);
      });
      console.log('[useFeed] Fetched schedules:', schedulesData.length);
      setSchedules(schedulesData);
      
      initialized.current = true;
      console.log('[useFeed] Initial data fetch completed successfully');
    } catch (err) {
      console.error('[useFeed] Error fetching initial feed data:', err);
      setError('Failed to fetch feed data');
    } finally {
      setLoading(false);
    }
  }, []); // Remove loading dependency to prevent infinite loop

  // Auto-fetch initial data on mount (only once)
  useEffect(() => {
    if (!initialized.current) {
      fetchInitialData();
    }
  }, []); // Empty dependency array - only run once on mount

  // Create a new feed post
  const createPost = async (postData: Omit<FeedPost, 'id' | 'createdAt' | 'updatedAt' | 'reactions' | 'comments' | 'readBy'>) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const now = new Date().toISOString();
      const newPost: Omit<FeedPost, 'id'> = {
        ...postData,
        createdAt: now,
        updatedAt: now,
        reactions: {},
        comments: [],
        readBy: [currentUser.uid]
      };

      const docRef = await addDoc(collection(db, 'feed_posts'), newPost);
      return docRef.id;
    } catch (err) {
      console.error('Error creating feed post:', err);
      throw new Error('Failed to create feed post');
    }
  };

  // Update a feed post
  const updatePost = async (postId: string, updates: Partial<FeedPost>) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error updating feed post:', err);
      throw new Error('Failed to update feed post');
    }
  };

  // Delete a feed post
  const deletePost = async (postId: string) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      // Find the post to get author information for logging
      const post = posts.find(p => p.id === postId);
      if (!post) throw new Error('Post not found');

      // Log the delete action for security purposes
      console.log('Post deletion requested:', {
        postId,
        postTitle: post.title,
        postAuthorId: post.authorId,
        postAuthorName: post.authorName,
        currentUserId: currentUser.uid,
        currentUserRole: currentUser.role,
        currentUserEmail: currentUser.email,
        timestamp: new Date().toISOString(),
        isAdminDelete: currentUser.role === 'admin' && currentUser.uid !== post.authorId
      });

      const postRef = doc(db, 'feed_posts', postId);
      await deleteDoc(postRef);
      
      console.log('Post deleted successfully:', {
        postId,
        deletedBy: currentUser.uid,
        userRole: currentUser.role,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error deleting feed post:', err);
      throw new Error('Failed to delete feed post');
    }
  };

  // Add comment to a post
  const addComment = async (postId: string, commentData: Omit<FeedComment, 'id' | 'createdAt' | 'updatedAt' | 'reactions' | 'isEdited'>) => {
    if (!currentUser) throw new Error('User not authenticated');

    console.log('addComment called with:', { postId, commentData, currentUser });

    try {
      const now = new Date().toISOString();
      const newComment: Omit<FeedComment, 'id'> = {
        ...commentData,
        createdAt: now,
        updatedAt: now,
        reactions: {},
        isEdited: false
      };

      console.log('New comment object:', newComment);

      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        comments: arrayUnion(newComment),
        updatedAt: new Date().toISOString()
      });

      console.log('Comment added to Firestore successfully');
    } catch (err) {
      console.error('Error adding comment:', err);
      throw new Error('Failed to add comment');
    }
  };

  // Update comment
  const updateComment = async (postId: string, commentIndex: number, newContent: string) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) throw new Error('Post not found');

      const updatedComments = [...post.comments];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],
        content: newContent,
        updatedAt: new Date().toISOString(),
        isEdited: true
      };

      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        comments: updatedComments,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error updating comment:', err);
      throw new Error('Failed to update comment');
    }
  };

  // Delete comment
  const deleteComment = async (postId: string, commentIndex: number) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) throw new Error('Post not found');

      const updatedComments = post.comments.filter((_, index) => index !== commentIndex);
      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        comments: updatedComments,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error deleting comment:', err);
      throw new Error('Failed to delete comment');
    }
  };

  // Add reaction to a post
  const addReaction = async (postId: string, reaction: 'like') => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        [`reactions.${currentUser.uid}`]: reaction
      });
    } catch (err) {
      console.error('Error adding reaction:', err);
      throw new Error('Failed to add reaction');
    }
  };

  // Remove reaction from a post
  const removeReaction = async (postId: string) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        [`reactions.${currentUser.uid}`]: deleteField()
      });
    } catch (err) {
      console.error('Error removing reaction:', err);
      throw new Error('Failed to remove reaction');
    }
  };

  // Mark post as read
  const markAsRead = async (postId: string) => {
    if (!currentUser) return;

    try {
      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        readBy: arrayUnion(currentUser.uid)
      });
    } catch (err) {
      console.error('Error marking post as read:', err);
    }
  };

  // Pin/unpin a post
  const togglePin = async (postId: string) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) throw new Error('Post not found');

      const postRef = doc(db, 'feed_posts', postId);
      await updateDoc(postRef, {
        isPinned: !post.isPinned,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error toggling pin:', err);
      throw new Error('Failed to toggle pin');
    }
  };

  // Create automated class schedule notification
  const createScheduleNotification = async (schedule: ClassSchedule) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const now = new Date().toISOString();
      const notificationPost: Omit<FeedPost, 'id' | 'createdAt' | 'updatedAt' | 'reactions' | 'comments' | 'readBy'> = {
        type: 'class_schedule',
        title: `📚 Class Reminder: ${schedule.classroomName}`,
        content: `Class "${schedule.subject}" is scheduled for ${new Date(schedule.scheduledTime).toLocaleString()}.\n\nDuration: ${schedule.duration} minutes\nInstructor: ${schedule.instructorName}\n\n${schedule.description}`,
        authorId: 'system',
        authorName: 'System Notification',
        authorRole: 'admin',
        category: 'Class Schedule',
        priority: 'high',
        isPinned: false,
        isAutomated: true,
        targetAudience: 'all',
        programFilter: schedule.category as 'ICAG' | 'CITG',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Expires in 24 hours
        attachments: schedule.materials || [],
        tags: ['class', 'reminder', schedule.category.toLowerCase()]
      };

      const docRef = await addDoc(collection(db, 'feed_posts'), {
        ...notificationPost,
        createdAt: now,
        updatedAt: now,
        reactions: {},
        comments: [],
        readBy: []
      });

      return docRef.id;
    } catch (err) {
      console.error('Error creating schedule notification:', err);
      throw new Error('Failed to create schedule notification');
    }
  };

  // Get filtered posts by category
  const getPostsByCategory = (category: string) => {
    return posts.filter(post => post.category === category);
  };

  // Get pinned posts
  const getPinnedPosts = () => {
    return posts.filter(post => post.isPinned);
  };

  // Get unread posts count
  const getUnreadCount = () => {
    if (!currentUser) return 0;
    return posts.filter(post => !post.readBy.includes(currentUser.uid)).length;
  };

  // Get upcoming classes (next 24 hours)
  const getUpcomingClasses = () => {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    return schedules.filter(schedule => {
      const scheduleTime = new Date(schedule.scheduledTime);
      return scheduleTime >= now && scheduleTime <= tomorrow && schedule.status === 'scheduled';
    });
  };

  return {
    posts,
    schedules,
    loading,
    error,
    createPost,
    updatePost,
    deletePost,
    addComment,
    updateComment,
    deleteComment,
    addReaction,
    removeReaction,
    markAsRead,
    togglePin,
    createScheduleNotification,
    getPostsByCategory,
    getPinnedPosts,
    getUnreadCount,
    getUpcomingClasses,
    fetchInitialData
  };
};
